import _, { groupBy, has, includes, nth, reduce, round, toNumber } from "lodash";
import { nanoid } from "nanoid";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { gql, useQuery as useUrqlQuery } from "urql";
import { CurrentUserContext } from "../components/CurrentUser";
import { DeepPartialGraph, DeepPartialOverride, GraphDeepPartialNotListed } from "../types";
import { Adr, HacpackQuery, HacpackQueryUnNumbersArguments, Handling, HandlingType, Terminal } from "../types/graph";

export const useQuery = () => new URLSearchParams(useLocation().search);
export const useCurrentUser = () => useContext(CurrentUserContext);

export const useId = (length?: number) => {
    const [id, setId] = useState<string>(() => nanoid(length));
    const resetId = useCallback(() => setId(nanoid(length)), [setId, length]);
    return { id, resetId };
};

export const useMounted = () => {
    const mounted = useRef<boolean>(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false
        };
    }, []);

    return mounted;
}

type UseTeusCountProps = Pick<DeepPartialOverride<Terminal, { teusLimit?: number, handlings?: GraphDeepPartialNotListed<Handling, 'type' | 'checkedIn' | 'checkedOut' | 'loadingUnitSizeFromGate' | 'loadingUnitSize'>[] }>, 'teusLimit' | 'handlings'>;

export const teusFromSizeString = (size: string | null | undefined) => {
    const n = Number(size?.trim()[0]);

    if (isNaN(n)) {
        return null;
    }

    return Math.ceil(n / 2)
}

export const useTeusCount = ({ handlings, teusLimit: limit }: UseTeusCountProps) => {

    const [current, expected, missing] = useMemo(() => handlings === undefined
        ? [undefined, undefined]
        : _(handlings)
            .filter(h => h.checkedOut !== true
                && (h.checkedIn === true || !(h.type === HandlingType.None || h.type === HandlingType.Transit)))
            .reduce((total, handling) => {

                const t = teusFromSizeString(handling.loadingUnitSizeFromGate) ?? teusFromSizeString(handling.loadingUnitSize) ?? null;

                const [ct, ed, ms] = total;

                const teus = t === null ? 2 : t;

                console.debug({ unitNumber: handling.loadingUnitIdentifier, type: handling.type, current: ct, expected: ed, missing: ms })



                return handling.checkedIn
                    ? teus === 0
                        ? [ct, ed, ms + 1]
                        : [ct + teus, ed, ms]
                    : [ct, ed + teus, ms];
            }, [0, 0, 0]), [handlings]);

    console.debug({ current, expected, missing, limit })

    const remaining = useMemo(() => (current === undefined || expected === undefined || limit === undefined)
        ? undefined
        : limit - (current + expected), [current, expected, limit]);

    return useMemo(() => ({ current, expected, missing, remaining }), [current, expected, missing, remaining])
};

export const useHandlingTypes = () => {
    const { t } = useTranslation('common');

    return useMemo(() => ({
        [HandlingType.Transit]: t('Transito'),
        [HandlingType.Boarding]: t('Imbarco'),
        [HandlingType.Unboarding]: t('Sbarco'),
        [HandlingType.TranshipmentBoarding]: t('Imbarco transhipment'),
        [HandlingType.TranshipmentUnboarding]: t('Sbarco transhipment')
    }), [t]);
}

//export { default as useDimensionStack } from "./useDimensionStack";
export { default as usePasswordStrength } from "./usePasswordStrength";


// #region Use
type UseUnNumbersProps = {
    unNumbers: string[]
};

type UseUnNumbersReturn = {
};

type UseUnNumbersQueryData = DeepPartialGraph<HacpackQuery>

const useUnNumbersQuery = gql<UseUnNumbersQueryData, HacpackQueryUnNumbersArguments>`
query unNumbers($unNumbers: [String!]!) {
    unNumbers(unNumbers:$unNumbers) {
        id
        un
        packingGroup
        hazardCls
    }
}`;

export const useUnNumbers = ({ unNumbers: unNumbersProp }: UseUnNumbersProps): UseUnNumbersReturn => {

    const [queryVar, setQueryVar] = useState<string[]>([]);
    const [unNumbers, setUnNumbers] = useState<Record<string, DeepPartialGraph<Adr>[] | null | undefined>>({});

    const [queryResult] = useUrqlQuery<UseUnNumbersQueryData, HacpackQueryUnNumbersArguments>({
        query: useUnNumbersQuery,
        variables: { unNumbers: queryVar },
        pause: queryVar.length === 0
    });

    const { data, fetching } = queryResult

    useEffect(() => {
        setUnNumbers(unNumbers => reduce(unNumbersProp, (unNumbers, un) => {
            if (has(unNumbers, un) === false) {
                unNumbers[un] = undefined;
            }

            return unNumbers;
        }, { ...unNumbers }));
    }, [unNumbersProp, setUnNumbers]);

    useEffect(() => {
        if (queryVar.length > 0) {
            return;
        }

        const newUn = reduce(unNumbers, (newUn, unNumber, un) => {
            if (unNumber === undefined && includes(queryVar, un) === false) {
                newUn.push(un);
            }

            return newUn;
        }, [] as string[])

        if (newUn.length === 0) {
            return;
        }

        setQueryVar(newUn)
    }, [unNumbers, queryVar, setQueryVar])

    useEffect(() => {
        if (fetching === true) {
            return;
        }

        setQueryVar((queryVar) => {
            setUnNumbers((unNumbers) => {
                const resultsByUn = groupBy(data?.unNumbers, 'un')

                return reduce(queryVar, (unNumbers, un, index) => {
                    if (un !== undefined) {
                        unNumbers[un] = resultsByUn[un] ?? null;
                    }

                    return unNumbers;
                }, { ...unNumbers });
            })

            return [];
        })

    }, [data, fetching, setQueryVar, setUnNumbers])

    return unNumbers;
}